import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';
import LovinLogo from './LovinLogo';
import { LEGAL_NAME, CRO_NUMBER, VAT_NUMBER, registeredOfficeOneLine } from '../utils/company';
import { useMarket } from '../hooks/useMarket';

const Footer = () => {
  const market = useMarket();
  const isUK = market.code === 'GB';
  const isUS = market.code === 'US';
  const tagline = isUK
    ? "The UK's honest property portal. Find your perfect home today."
    : isUS
      ? "The US's honest property portal. Find your perfect home today."
      : "Ireland's leading property website. Find your perfect home today.";
  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
          {/* Brand */}
          <div>
            <div className="mb-4 [&_*]:!text-white" style={{ color: '#fff' }}>
              <LovinLogo word="Home" size={32} onDark />
            </div>
            <p className="text-sm">
              {tagline}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to={market.withPrefix('/buy')} className="hover:text-[#7042cc] transition-colors">
                  Buy
                </Link>
              </li>
              <li>
                <Link to={market.withPrefix('/rent')} className="hover:text-[#7042cc] transition-colors">
                  Rent
                </Link>
              </li>
              <li>
                <Link to={market.withPrefix('/share')} className="hover:text-[#7042cc] transition-colors">
                  Share
                </Link>
              </li>
              <li>
                <Link to={market.withPrefix('/new-homes')} className="hover:text-[#7042cc] transition-colors">
                  New Homes
                </Link>
              </li>
              <li>
                <Link to={market.withPrefix('/agent-match')} className="hover:text-[#7042cc] transition-colors font-semibold text-purple-300" data-testid="footer-agent-match-link">
                  Agent Match →
                </Link>
              </li>
              <li>
                <Link to={market.withPrefix('/agent-match/pricing')} className="hover:text-[#7042cc] transition-colors" data-testid="footer-agent-match-pricing-link">
                  Agent Match pricing
                </Link>
              </li>
            </ul>
          </div>

          {/* Market Data */}
          <div data-testid="footer-market-data-col">
            <h3 className="text-white font-semibold mb-4">Market Data</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to={market.withPrefix('/real-cost')} className="hover:text-[#7042cc] transition-colors" data-testid="footer-real-cost-link">
                  My Real Cost
                </Link>
              </li>
              <li>
                <Link to={market.withPrefix('/area-prices')} className="hover:text-[#7042cc] transition-colors" data-testid="footer-area-prices-link">
                  Area Prices
                </Link>
              </li>
              <li>
                <Link to={market.withPrefix('/price-index')} className="hover:text-[#7042cc] transition-colors" data-testid="footer-price-index-link">
                  Real Price Index
                </Link>
              </li>
              <li>
                <Link to={market.withPrefix('/map')} className="hover:text-[#7042cc] transition-colors">
                  Property Map
                </Link>
              </li>
              <li>
                <Link to={market.withPrefix('/for-agents')} className="hover:text-[#7042cc] transition-colors font-semibold text-purple-300" data-testid="footer-for-agents-link">
                  For Agents →
                </Link>
              </li>
            </ul>
          </div>

          {/* Popular areas — market-aware so /uk visitors see London/
              Manchester/Edinburgh instead of Rathmines/Howth etc. */}
          <div>
            <h3 className="text-white font-semibold mb-4">Popular areas</h3>
            <ul className="space-y-2 text-sm" data-testid="footer-areas">
              {(isUK
                ? [
                    { label: 'Greater London', slug: 'greater-london', tid: 'london' },
                    { label: 'Greater Manchester', slug: 'greater-manchester', tid: 'manchester' },
                    { label: 'Edinburgh', slug: 'edinburgh', tid: 'edinburgh' },
                    { label: 'Cardiff', slug: 'cardiff', tid: 'cardiff' },
                    { label: 'Belfast', slug: 'belfast', tid: 'belfast' },
                  ]
                : isUS
                  ? [
                      { label: 'New York', slug: 'new-york', tid: 'new-york' },
                      { label: 'Los Angeles', slug: 'los-angeles', tid: 'los-angeles' },
                      { label: 'Chicago', slug: 'chicago', tid: 'chicago' },
                      { label: 'Miami', slug: 'miami', tid: 'miami' },
                      { label: 'Boston', slug: 'boston', tid: 'boston' },
                    ]
                  : [
                      { label: 'Rathmines, D6', slug: 'dublin/rathmines', tid: 'rathmines' },
                      { label: 'Howth, D13', slug: 'dublin/howth', tid: 'howth' },
                      { label: 'Foxrock, D18', slug: 'dublin/foxrock', tid: 'foxrock' },
                      { label: 'Drumcondra, D9', slug: 'dublin/drumcondra', tid: 'drumcondra' },
                      { label: 'Sandymount, D4', slug: 'dublin/sandymount', tid: 'sandymount' },
                    ]
              ).map((a) => (
                <li key={a.slug}>
                  <Link
                    to={isUK || isUS ? market.withPrefix(`/buy/${a.slug}`) : `/areas/${a.slug}`}
                    className="hover:text-[#7042cc] transition-colors"
                    data-testid={`footer-area-${a.tid}`}
                  >
                    {a.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Help */}
          <div>
            <h3 className="text-white font-semibold mb-4">Help</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to={market.withPrefix('/about')} className="hover:text-[#7042cc] transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to={market.withPrefix('/contact')} className="hover:text-[#7042cc] transition-colors">
                  Contact
                </Link>
              </li>
              <li>
                <Link to={market.withPrefix('/faq')} className="hover:text-[#7042cc] transition-colors">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to={market.withPrefix('/terms')} className="hover:text-[#7042cc] transition-colors">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to={market.withPrefix('/privacy')} className="hover:text-[#7042cc] transition-colors" data-testid="footer-privacy-link">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to={market.withPrefix('/cookies')} className="hover:text-[#7042cc] transition-colors" data-testid="footer-cookies-link">
                  Cookie Policy
                </Link>
              </li>
              <li>
                <Link to={market.withPrefix('/refund-policy')} className="hover:text-[#7042cc] transition-colors" data-testid="footer-refund-link">
                  Refund Policy
                </Link>
              </li>
              <li>
                <Link to={market.withPrefix('/account/privacy')} className="hover:text-[#7042cc] transition-colors" data-testid="footer-privacy-settings-link">
                  Privacy &amp; Data Controls
                </Link>
              </li>
              <li>
                <Link to={market.withPrefix('/trust')} className="hover:text-[#7042cc] transition-colors" data-testid="footer-trust-link">
                  Trust &amp; Security
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-semibold mb-4">Connect</h3>
            <div className="flex space-x-4">
              <a
                href="#"
                className="text-gray-400 hover:text-[#7042cc] transition-colors"
              >
                <Facebook className="h-6 w-6" />
              </a>
              <a
                href="#"
                className="text-gray-400 hover:text-[#7042cc] transition-colors"
              >
                <Twitter className="h-6 w-6" />
              </a>
              <a
                href="#"
                className="text-gray-400 hover:text-[#7042cc] transition-colors"
              >
                <Instagram className="h-6 w-6" />
              </a>
              <a
                href="#"
                className="text-gray-400 hover:text-[#7042cc] transition-colors"
              >
                <Linkedin className="h-6 w-6" />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-xs text-center space-y-1.5" data-testid="footer-legal">
          <p className="text-sm">&copy; 2026 {LEGAL_NAME}. All rights reserved.</p>
          <p className="text-gray-400">
            Trading as <span className="font-semibold">lovinahome</span> · Registered in Ireland ·{' '}
            CRO {CRO_NUMBER} · VAT {VAT_NUMBER}
          </p>
          <p className="text-gray-500">{registeredOfficeOneLine()}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
